import * as React from 'react'
import { AutopayConsentChoice } from '@/components/AutopayConsentChoice'
import { Button } from '@/components/ui/button'
import { SummaryRow, SummarySection } from '@/pages/PublicJoinFormPage.summary'
import { displayCustomFieldValue } from '@/pages/PublicJoinFormPage.helpers'
import type {
  DiscountQuote,
  JoinCustomFieldValue,
  OrganizationForm,
  PersonForm,
  PublicJoinCustomField,
  PublicMembershipLevel,
} from '@/pages/PublicJoinFormPage.types'
import { discountReductionLabel } from '@/pages/PublicJoinFormPage.types'

export function ReviewStep({
  selectedLevel,
  organization,
  person,
  customFields,
  customFieldValues,
  website,
  submitting,
  discountQuote,
  enrollAutopay,
  onBack,
  onWebsiteChange,
  onEnrollAutopayChange,
  onSubmit,
}: {
  selectedLevel: PublicMembershipLevel
  organization: OrganizationForm
  person: PersonForm
  customFields: PublicJoinCustomField[]
  customFieldValues: Record<string, JoinCustomFieldValue>
  website: string
  submitting: boolean
  discountQuote: DiscountQuote | null
  enrollAutopay: boolean
  onBack: () => void
  onWebsiteChange: (value: string) => void
  onEnrollAutopayChange: (checked: boolean) => void
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void
}) {
  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <header>
        <h1 className="text-subheading font-semibold text-foreground">Review application</h1>
        <p className="mt-1 text-body-sm text-muted-foreground">
          Confirm these details before submitting to the association.
        </p>
      </header>
      <SummarySection title="Membership Level">
        <SummaryRow label="Level" value={selectedLevel.name} />
        <SummaryRow
          label="Fee"
          value={
            selectedLevel.free ? selectedLevel.fee : (selectedLevel.subtotal ?? selectedLevel.fee)
          }
        />
        {selectedLevel.tax_rate && (
          <SummaryRow
            label={`Tax — ${selectedLevel.tax_rate.name} (${selectedLevel.tax_rate.percentage}%)`}
            value={selectedLevel.tax}
          />
        )}
        {discountQuote && (
          <>
            <SummaryRow
              label="Discount Code"
              value={`${discountQuote.code} (${discountReductionLabel(discountQuote)})`}
            />
            <SummaryRow label="Reduction" value={`−${discountQuote.reduction}`} />
            <SummaryRow label="Total" value={discountQuote.total} />
          </>
        )}
        {!discountQuote && selectedLevel.tax_rate && (
          <SummaryRow label="Total" value={selectedLevel.total} />
        )}
        <SummaryRow label="Renewal" value={selectedLevel.renewal} />
      </SummarySection>
      <SummarySection title="Your Details">
        <SummaryRow label="Name" value={person.name} />
        <SummaryRow label="Email" value={person.email || 'None'} />
        <SummaryRow label="Phone" value={person.phone || 'None'} />
      </SummarySection>
      <OrganizationSummary level={selectedLevel} organization={organization} />
      {customFields.length > 0 && (
        <SummarySection title="Additional Questions">
          {customFields.map((field) => (
            <SummaryRow
              key={field.id}
              label={field.label}
              value={displayCustomFieldValue(customFieldValues[String(field.id)])}
            />
          ))}
        </SummarySection>
      )}
      {selectedLevel.autopay_offer && (
        <AutopayConsentChoice
          offer={selectedLevel.autopay_offer}
          checked={enrollAutopay}
          onCheckedChange={onEnrollAutopayChange}
        />
      )}
      <input
        type="text"
        name="website"
        value={website}
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
        onChange={(event) => onWebsiteChange(event.target.value)}
      />
      <div className="flex gap-2 pt-1">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit application'}
        </Button>
      </div>
    </form>
  )
}

function OrganizationSummary({
  level,
  organization,
}: {
  level: PublicMembershipLevel
  organization: OrganizationForm
}) {
  if (level.kind !== 'organization') return null
  return (
    <SummarySection title="Organization Details">
      <SummaryRow label="Name" value={organization.name} />
      <SummaryRow label="Email" value={organization.email || 'None'} />
      <SummaryRow label="Phone" value={organization.phone || 'None'} />
    </SummarySection>
  )
}
